<template>
  <div class="chart-wrap" v-if="dataPoints.length">
    <svg :viewBox="`0 0 ${width} ${height}`" preserveAspectRatio="xMidYMid meet" class="chart">
      <!-- Axes -->
      <line class="axis" :x1="leftPad" :y1="height - bottomPad" :x2="width - rightPad" :y2="height - bottomPad" />
      <line class="axis" :x1="leftPad" :y1="topPad" :x2="leftPad" :y2="height - bottomPad" />
      <!-- Y ticks -->
      <g v-for="t in yTicks" :key="t.value">
        <line class="grid" :x1="leftPad" :x2="width - rightPad" :y1="yPos(t.value)" :y2="yPos(t.value)" />
        <text class="tick" :x="leftPad - 6" :y="yPos(t.value) + 3">{{ t.label }}</text>
      </g>
      <!-- Bars -->
      <g v-for="(d, i) in dataPoints" :key="d.date">
        <rect
          class="bar"
          :x="leftPad + i * barStep + barGap/2"
          :y="yPos(d.total)"
          :width="barWidth"
          :height="Math.max(0, (height - bottomPad) - yPos(d.total))"
        >
          <title>{{ d.date }}: {{ d.total }}</title>
        </rect>
      </g>
      <!-- X ticks (sampled) -->
      <g v-for="i in xTickIdx" :key="i">
        <line class="tick-line" :x1="leftPad + i * barStep + barStep/2" :y1="height - bottomPad" :x2="leftPad + i * barStep + barStep/2" :y2="height - bottomPad + 4" />
        <text class="tick x" :x="leftPad + i * barStep + barStep/2" :y="height - bottomPad + 12">{{ dataPoints[i].date }}</text>
      </g>
    </svg>
    <div class="legend">
      <span class="dot"></span>
      <span>Completed orders per day</span>
      <span class="muted" v-if="totalCount">Total: {{ totalCount }}</span>
    </div>
  </div>
  <div v-else class="muted small">No data for selected filters.</div>
</template>

<script>
export default {
  name: 'SalesVolumeChart',
  props: {
    series: { type: Array, required: true },
  },
  data() {
    return {
      width: 720,
      height: 200,
      barGap: 2,
      leftPad: 36,
      rightPad: 8,
      topPad: 8,
      bottomPad: 28,
    }
  },
  computed: {
    dataPoints() {
      const arr = Array.isArray(this.series) ? this.series : []
      return arr.map(x => ({ date: x.date, total: Number(x.total || 0) }))
    },
    maxY() {
      const m = Math.max(1, ...this.dataPoints.map(d => d.total || 0))
      // Nice-ish rounding
      const pow10 = Math.pow(10, Math.floor(Math.log10(m)))
      const n = Math.ceil(m / pow10)
      const top = (n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow10
      return top
    },
    barStep() {
      const n = this.dataPoints.length || 1
      const innerWidth = this.width - this.leftPad - this.rightPad
      return innerWidth / n
    },
    barWidth() {
      return Math.max(1, this.barStep - this.barGap)
    },
    totalCount() {
      return this.dataPoints.reduce((a, b) => a + (b.total || 0), 0)
    },
    yTicks() {
      const ticks = [0, this.maxY / 2, this.maxY]
      return ticks.map(v => ({ value: v, label: Math.round(v) }))
    },
    xTickIdx() {
      const n = this.dataPoints.length
      if (n <= 1) return [0]
      const approx = Math.max(2, Math.floor(this.width / 120))
      const step = Math.max(1, Math.floor(n / approx))
      const idx = []
      for (let i = 0; i < n; i += step) idx.push(i)
      if (idx[idx.length - 1] !== n - 1) idx.push(n - 1)
      return idx
    }
  },
  methods: {
    yPos(val) {
      const innerH = this.height - this.bottomPad - this.topPad
      const ratio = Math.min(1, (val || 0) / this.maxY)
      return this.topPad + (1 - ratio) * innerH
    }
  }
}
</script>

<style scoped>
.chart-wrap { width: 100%; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 8px; }
.chart { width: 100%; height: 220px; display: block; }
.bar { fill: #2563eb; opacity: 0.8; }
.axis { stroke: #9ca3af; stroke-width: 1; vector-effect: non-scaling-stroke; }
.grid { stroke: #eef2f7; stroke-width: 1; vector-effect: non-scaling-stroke; }
.tick-line { stroke: #9ca3af; stroke-width: 1; vector-effect: non-scaling-stroke; }
.tick { font-size: 10px; fill: #6b7280; text-anchor: end; }
.tick.x { text-anchor: middle; }
.legend { display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 12px; color: #111827; }
.legend .dot { width: 10px; height: 10px; background: #2563eb; border-radius: 9999px; display: inline-block; }
.muted { color: #6b7280; }
.small { font-size: 12px; }
</style>
